import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { ArrheniusPlot } from "@/components/tools/chem-physical/arrhenius-plot";
import {
  type ArrheniusStrings,
  arrheniusMessages,
  DEFAULT_SIG,
  ENERGY_OPTIONS,
  eaText,
  R_TEXT,
  SolvedCell,
  TEMPERATURE_OPTIONS,
} from "@/components/tools/chem-physical/arrhenius-shared";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  fitArrhenius,
  type RiseSolution,
  type RiseTerm,
  riseFactor,
  solveRise,
} from "@/lib/tool-kit/chem-physical/arrhenius";
import { MOLAR_ENERGY_UNITS } from "@/lib/tool-kit/chem-physical/gibbs";
import { unitFactor, unitSymbol } from "@/lib/tool-kit/chem-physical/heat";
import { temperatureSymbol } from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

/* ------------------------------------------------ rate change with T */

const TABLE_EAS = [25, 50, 75, 100] as const;

function RiseAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: RiseSolution;
  texts: { ea: string; t: string; dt: string; factor: string };
  units: { ea: string; t: string };
  sig: number;
  strings: ArrheniusStrings;
}) {
  const fmt = useToolNumber();
  const { unknown } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const answerText =
    unknown === "factor"
      ? `${formatSig(fmt, solution.factor, sig)}×`
      : eaText(fmt, solution.ea, units.ea, sig);
  const label = unknown === "factor" ? strings.termFactor : strings.termEa;
  const per10 = solution.factor ** (10 / solution.dt);
  const conversions: string[] = [];
  if (units.t !== "K") {
    conversions.push(
      t(strings.convertLine, {
        label: strings.termT,
        value: texts.t.trim(),
        unit: temperatureSymbol(units.t),
        converted: `${p(solution.t)} K`,
      })
    );
  }
  if (unknown !== "ea" && unitFactor(MOLAR_ENERGY_UNITS, units.ea) !== 1) {
    conversions.push(
      t(strings.convertLine, {
        label: strings.termEa,
        value: texts.ea.trim(),
        unit: unitSymbol(MOLAR_ENERGY_UNITS, units.ea),
        converted: `${p(solution.ea)} J/mol`,
      })
    );
  }
  const substituted = t(
    unknown === "factor"
      ? strings.riseSubstitutedFactor
      : strings.riseSubstitutedEa,
    {
      ea: p(solution.ea),
      t: p(solution.t),
      t2: p(solution.t + solution.dt),
      factor: p(solution.factor),
      r: R_TEXT,
      result:
        unknown === "factor"
          ? formatSig(fmt, solution.factor, sig + 1)
          : `${formatSig(fmt, solution.ea, sig + 1)} J/mol`,
    }
  );
  const rows = TABLE_EAS.map((kj) => {
    const factor = riseFactor(kj * 1000, solution.t, solution.dt);
    return [`${kj} kJ/mol`, `${formatSig(fmt, factor, 3)}×`];
  });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={label} value={answerText} />
        <StatCard
          label={strings.perTenDegrees}
          value={`${formatSig(fmt, per10, sig)}×`}
        />
        <StatCard
          label={strings.temperatureRise}
          value={`${formatSig(fmt, solution.dt, sig)} K`}
        />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.riseEquation}</p>
        {unknown === "factor" ? null : <p>{strings.riseRearrangedEa}</p>}
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
      </div>
      <div className="space-y-2">
        <p className="font-medium text-foreground text-sm">
          {strings.riseTableHeading}
        </p>
        <DataTable
          head={[strings.columnEa, strings.columnFactor]}
          rows={rows}
        />
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, { label, value: answerText })}
      />
    </>
  );
}

export function RisePanel({
  strings,
  sigChoice,
}: {
  strings: ArrheniusStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<RiseTerm>("factor");
  const [text, setText] = useState({
    ea: "50.0",
    t: "25.0",
    dt: "10.0",
    factor: "",
  });
  const [unit, setUnit] = useState({ ea: "kJ", t: "C" });
  const outcome = solveRise({ unknown, text, unit });
  const typed =
    unknown === "factor"
      ? [text.ea, text.t, text.dt]
      : [text.factor, text.t, text.dt];
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<string, string> = {
    ea: strings.termEa,
    t: strings.termT,
    dt: strings.temperatureRise,
    factor: strings.termFactor,
  };
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="arr3-solve"
        onChange={setUnknown}
        options={[
          { value: "factor", label: strings.termFactor },
          { value: "ea", label: strings.termEa },
        ]}
        value={unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityInput
          id="arr3-ea"
          label={
            unknown === "ea"
              ? t(strings.answerSuffix, { label: labels.ea })
              : labels.ea
          }
          onUnitChange={(value) => setUnit({ ...unit, ea: value })}
          onValueChange={(value) => setText({ ...text, ea: value })}
          solved={unknown === "ea"}
          unit={unit.ea}
          unitLabel={strings.unitEa}
          units={ENERGY_OPTIONS}
          value={text.ea}
        />
        {unknown === "factor" ? (
          <SolvedCell
            id="arr3-factor"
            label={t(strings.answerSuffix, { label: labels.factor })}
            text={strings.solvedCell}
          />
        ) : (
          <TextField
            hint={strings.factorHint}
            id="arr3-factor"
            label={labels.factor}
            numeric
            onChange={(value) => setText({ ...text, factor: value })}
            value={text.factor}
          />
        )}
        <QuantityInput
          id="arr3-t"
          label={labels.t}
          onUnitChange={(value) => setUnit({ ...unit, t: value })}
          onValueChange={(value) => setText({ ...text, t: value })}
          unit={unit.t}
          unitLabel={strings.unitTemperature}
          units={TEMPERATURE_OPTIONS}
          value={text.t}
        />
        <TextField
          hint={strings.riseHint}
          id="arr3-dt"
          label={labels.dt}
          numeric
          onChange={(value) => setText({ ...text, dt: value })}
          value={text.dt}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <RiseAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={arrheniusMessages(strings)}
          />
        )}
      </section>
    </>
  );
}

/* ------------------------------------------------------ plot / fit */

const HI_DATA =
  "555 3.52e-7\n575 1.22e-6\n645 8.59e-5\n700 1.16e-3\n781 3.95e-2";

export function PlotPanel({
  strings,
  sigChoice,
}: {
  strings: ArrheniusStrings;
  sigChoice: string;
}) {
  const fmt = useToolNumber();
  const [data, setData] = useState(HI_DATA);
  const [tempUnit, setTempUnit] = useState("K");
  const [eaUnit, setEaUnit] = useState("kJ");
  const outcome = fitArrhenius(data, tempUnit);
  const sig = sigChoice === "match" ? DEFAULT_SIG : Number(sigChoice);
  const labels = { data: strings.dataLabel };
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextAreaField
          hint={strings.dataHint}
          id="arr4-data"
          label={strings.dataLabel}
          onChange={setData}
          rows={7}
          value={data}
        />
        <div className="space-y-4">
          <SelectField
            id="arr4-t"
            label={strings.unitTemperature}
            onChange={setTempUnit}
            options={TEMPERATURE_OPTIONS}
            value={tempUnit}
          />
          <SelectField
            id="arr4-ea"
            label={strings.unitEa}
            onChange={setEaUnit}
            options={ENERGY_OPTIONS}
            value={eaUnit}
          />
          <button
            className={BUTTON_SMALL}
            onClick={() => {
              setData(HI_DATA);
              setTempUnit("K");
            }}
            type="button"
          >
            {strings.exampleHi}
          </button>
        </div>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.termEa}
                value={eaText(fmt, outcome.ea, eaUnit, sig)}
              />
              <StatCard
                label={strings.termA}
                value={formatSig(fmt, outcome.a, sig)}
              />
              <StatCard
                label={strings.rSquared}
                value={formatSig(fmt, outcome.r2, 4)}
              />
            </StatGrid>
            {outcome.points.length === 2 ? (
              <Notice>{strings.twoPointsNote}</Notice>
            ) : null}
            {outcome.ea < 0 ? <Notice>{strings.negativeEaNote}</Notice> : null}
            <ArrheniusPlot fit={outcome} strings={strings} />
            <DataTable
              head={[
                strings.columnT,
                strings.columnK,
                strings.columnInverseT,
                strings.columnLnK,
              ]}
              rows={outcome.points.map((point) => [
                formatPlain(fmt, point.t),
                formatSig(fmt, point.k, sig),
                formatSig(fmt, point.x, 4),
                formatSig(fmt, point.y, 4),
              ])}
            />
            <div className={WORKING_BOX}>
              <p className="font-medium font-sans text-foreground">
                {strings.working}
              </p>
              <p>{strings.fitEquation}</p>
              <p>
                {t(strings.fitSlope, {
                  slope: formatSig(fmt, outcome.slope, 5),
                  intercept: formatSig(fmt, outcome.intercept, 5),
                })}
              </p>
              <p>
                {t(strings.fitEa, {
                  slope: formatPlain(fmt, outcome.slope),
                  r: R_TEXT,
                  ea: formatSig(fmt, outcome.ea, sig + 1),
                })}
              </p>
              <p>
                {t(strings.fitA, {
                  intercept: formatPlain(fmt, outcome.intercept),
                  a: formatSig(fmt, outcome.a, sig + 1),
                })}
              </p>
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copy}
              text={t(strings.copyText, {
                label: strings.termEa,
                value: eaText(fmt, outcome.ea, eaUnit, sig),
              })}
            />
          </>
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={{
              ...arrheniusMessages(strings),
              badLine:
                "line" in outcome && outcome.line
                  ? t(strings.errorBadLine, { line: String(outcome.line) })
                  : strings.errorBadLine,
            }}
          />
        )}
      </section>
    </>
  );
}
